import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { t } from "../translations";

export type JobMatchingStatus = "loading" | "delayed" | "timeout" | null;

interface JobMatchingStatusDialogProps {
  status: JobMatchingStatus;
  onDismiss: () => void;
  language: string;
}

const getStatusConfig = (status: JobMatchingStatus, language: string) => {
  switch (status) {
    case "loading":
      return {
        title: t("fetchingJobs", language),
        message: t("fetchingJobsMessage", language),
        icon: <CircularProgress size={48} />,
        showWarning: false,
      };
    case "delayed":
      return {
        title: t("stillWorking", language),
        message: t("stillWorkingMessage", language),
        icon: <CircularProgress size={48} />,
        showWarning: true,
      };
    case "timeout":
      return {
        title: t("requestTimeout", language),
        message: t("requestTimeoutMessage", language),
        icon: <Warning sx={{ fontSize: 48, color: "error.main" }} />,
        showWarning: true,
      };
    default:
      return null;
  }
};

export const JobMatchingStatusDialog: React.FC<JobMatchingStatusDialogProps> = ({
  status,
  onDismiss,
  language,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const config = getStatusConfig(status, language);
  const handleClose = () => {
    if (status === "timeout" && onDismiss) {
      onDismiss();
    }
  };

  if (!config || !status) return null;

  return (
    <Dialog
      open={!!status}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={status !== "timeout"}
      PaperProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
          borderRadius: isMobile ? "12px" : "16px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          margin: isMobile ? "16px" : "32px",
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 4 },
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 2.5,
          p: { xs: 2, sm: 3 },
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "8px",
          }}
        >
          {config.icon}
        </div>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1.25rem",
              sm: "1.5rem",
            },
            color: config.showWarning && status === "timeout" ? "error.main" : "text.primary",
          }}
        >
          {config.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: {
              xs: "0.9375rem",
              sm: "1rem",
            },
            lineHeight: 1.6,
            color: "text.secondary",
            maxWidth: "400px",
          }}
        >
          {config.message}
        </Typography>

        {config.showWarning && status !== "timeout" && (
          <Typography
            variant="caption"
            sx={{
              fontSize: {
                xs: "0.8125rem",
                sm: "0.875rem",
              },
              color: "warning.main",
              fontStyle: "italic",
              mt: 1,
            }}
          >
            {t("mayTakeTwoMinutes", language)}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};